import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  FrontClearanceForm,
  FrontClearanceListMeta,
  FrontClearanceManufacturerFilter,
  FrontClearanceMasterModelFilter,
  FrontClearanceRow,
  FrontClearanceYearFilter,
} from "../types";
import { initialFrontClearanceForm, initialState } from "./initialState";

export const sliceKey = "frontClearanceState";

type FrontClearanceListSuccessPayload = {
  data: FrontClearanceRow[];
  meta: FrontClearanceListMeta;
};

const frontClearanceSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getFrontClearanceList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getFrontClearanceListSuccess(
      state,
      action: PayloadAction<FrontClearanceListSuccessPayload>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getFrontClearanceListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<FrontClearanceListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    setManufacturerFilter(
      state,
      action: PayloadAction<FrontClearanceManufacturerFilter>
    ) {
      state.manufacturerFilter = action.payload;
    },

    setMasterModelFilter(
      state,
      action: PayloadAction<FrontClearanceMasterModelFilter>
    ) {
      state.masterModelFilter = action.payload;
    },

    setYearFilter(state, action: PayloadAction<FrontClearanceYearFilter>) {
      state.yearFilter = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialFrontClearanceForm;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof FrontClearanceForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    resetForm(state) {
      state.form = initialFrontClearanceForm;
    },

    addFrontClearance(state) {
      state.saving = true;
      state.error = null;
    },

    addFrontClearanceSuccess(state) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialFrontClearanceForm;
      state.error = null;
    },

    addFrontClearanceError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
  },
});

export const { actions: frontClearanceActions, reducer } = frontClearanceSlice;
